import { useT } from '@/lib/i18n';
import { useSourceStore } from '@/lib/state/sourceStore';
import { useState } from 'react';
import { PdfUploader } from './PdfUploader';
import { PhotoCapture } from './PhotoCapture';
import { SourceChip } from './SourceChip';
import { TextPasteForm } from './TextPasteForm';

type Tab = 'pdf' | 'photo' | 'text';

interface SourceStackerProps {
  onGenerate?: () => void;
}

export function SourceStacker({ onGenerate }: SourceStackerProps) {
  const { t, lang } = useT();
  const sources = useSourceStore((s) => s.sources);
  const removeSource = useSourceStore((s) => s.removeSource);
  const totalChars = useSourceStore((s) => s.totalChars());
  const [tab, setTab] = useState<Tab>('text');

  const TABS: { id: Tab; label: string; icon: string }[] = [
    { id: 'pdf', label: t('source.tab_pdf'), icon: '📄' },
    { id: 'photo', label: t('source.tab_photo'), icon: '📷' },
    { id: 'text', label: t('source.tab_text'), icon: '📝' },
  ];

  return (
    <section className="flex flex-col gap-4" data-testid="source-stacker">
      <div className="flex flex-col gap-2">
        <h2 className="font-gloock text-[20px] text-ink">{t('source.your_sources')}</h2>
        {sources.length === 0 ? (
          <p className="font-caveat text-ink-soft text-[18px]">{t('source.empty_prompt')}</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="source-list" aria-live="polite">
            {sources.map((s) => (
              <SourceChip key={s.id} source={s} onRemove={removeSource} />
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex gap-2" role="tablist" aria-label="Type de source">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={[
                  'flex items-center gap-2 px-3 py-2 border-2 border-ink rounded-control font-hand text-[14px] transition-transform',
                  active
                    ? 'bg-accent-yellow shadow-ink-sm'
                    : 'bg-white hover:translate-y-[-1px] shadow-ink-sm/0',
                ].join(' ')}
              >
                <span aria-hidden>{t.icon}</span>
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
        <div
          className="border-2 border-ink rounded-card bg-white/60 p-3 shadow-ink-sm"
          role="tabpanel"
        >
          {tab === 'pdf' ? <PdfUploader /> : null}
          {tab === 'photo' ? <PhotoCapture /> : null}
          {tab === 'text' ? <TextPasteForm /> : null}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="font-caveat text-ink-soft text-[16px]">
          {sources.length === 0
            ? t('source.none')
            : `${t(sources.length === 1 ? 'source.count_one' : 'source.count_other', {
                n: sources.length,
              })} · ${t('source.chars', {
                n: totalChars.toLocaleString(lang === 'en' ? 'en-US' : 'fr-FR'),
              })}`}
        </span>
        <button
          type="button"
          onClick={onGenerate}
          disabled={sources.length === 0}
          className="px-5 py-3 bg-accent-yellow text-ink font-gloock text-[18px] border-[2.5px] border-ink rounded-card shadow-ink hover:translate-y-[-1px] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 transition-transform"
          data-testid="generate-button"
        >
          {t('source.generate')}
        </button>
      </div>
    </section>
  );
}
